import { ConfigManager } from '@unidev-hub/config';
import { Logger } from '@unidev-hub/logger';
import { DeepPartial } from '../../types';

/**
 * Default configuration values by environment
 */
export const defaultConfigs: Record<string, DeepPartial<ServerConfig>> = {
  // Base configuration for all environments
  base: {
    server: {
      port: 3000,
      host: '0.0.0.0',
      shutdownTimeout: 10000, // ms
    },
    api: {
      basePath: '/api',
      cors: true,
      bodyLimit: '1mb',
      compression: true,
      requestLogging: true,
      correlationId: true,
      docs: {
        enabled: true,
        endpoint: '/api-docs',
        title: 'API Documentation',
        version: '1.0.0',
        description: 'API Documentation'
      }
    },
    database: {
      required: true,
      type: 'postgres',
      maxConnections: 10,
      idleTimeout: 30, // seconds
      connectTimeout: 5 // seconds
    },
    auth: {
      required: true
    },
    health: {
      enabled: true,
      endpoint: '/health',
      memory: {
        threshold: 85 // percentage
      }
    },
    logging: {
      level: 'info',
      prettyPrint: true
    }
  },

  // Development environment overrides
  development: {
    database: {
      host: 'localhost',
      port: 5432,
      user: 'devuser',
      password: 'devpassword',
      database: 'devdb',
      ssl: false,
      url: 'postgresql://devuser:devpassword@localhost:5432/devdb'
    },
    auth: {
      type: 'none', // No auth by default in development
      required: false
    },
    logging: {
      level: 'debug',
      prettyPrint: true
    }
  },

  // Test environment overrides
  test: {
    server: {
      port: 0 // Use random available port
    },
    database: {
      host: 'localhost',
      port: 5432,
      user: 'testuser',
      password: 'testpassword',
      database: 'testdb',
      ssl: false,
      url: 'postgresql://testuser:testpassword@localhost:5432/testdb'
    },
    auth: {
      type: 'none',
      required: false
    },
    api: {
      docs: {
        enabled: false
      }
    },
    logging: {
      level: 'warn',
      prettyPrint: false
    }
  },

  // Production environment overrides
  production: {
    server: {
      port: process.env.PORT ? parseInt(process.env.PORT, 10) : 3000
    },
    api: {
      helmet: true,
      requestLogging: true,
      docs: {
        enabled: false // Default to disabled in production
      }
    },
    auth: {
      type: 'jwt', // Default to JWT in production
      required: true
    },
    database: {
      ssl: true,
      required: true
    },
    logging: {
      level: 'info',
      prettyPrint: false
    }
  }
};

/**
 * Set default configuration values
 *
 * @param config ConfigManager instance
 * @param logger Logger instance
 * @param environment Current environment
 * @param customDefaults Custom defaults to apply
 */
export function setDefaultConfig(
  config: ConfigManager,
  logger: Logger,
  environment: string,
  customDefaults?: DeepPartial<ServerConfig>
): void {
  logger.debug('Setting default configuration values');

  // Start with base config
  if (defaultConfigs.base) {
    applyConfig(config, defaultConfigs.base);
  }

  // Apply environment-specific defaults
  if (defaultConfigs[environment]) {
    logger.debug(`Applying ${environment} environment defaults`);
    applyConfig(config, defaultConfigs[environment]);
  }

  // Apply custom defaults if provided
  if (customDefaults) {
    logger.debug('Applying custom defaults');
    applyConfig(config, customDefaults);
  }

  logger.debug('Default configuration applied');
}

/**
 * Apply configuration values recursively
 */
function applyConfig(config: ConfigManager, values: Record<string, any>, prefix = ''): void {
  // Check if values is a valid object
  if (!values || typeof values !== 'object' || Array.isArray(values)) {
    return;
  }

  // Iterate through keys using Object.entries to avoid Symbol.iterator issue
  Object.entries(values).forEach(([key, value]) => {
    const fullKey = prefix ? `${prefix}.${key}` : key;

    if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
      // Recurse for nested objects
      applyConfig(config, value, fullKey);
    } else {
      // Set leaf value
      config.set(fullKey, value);
    }
  });
}

/**
 * Server configuration structure
 */
export interface ServerConfig {
  server: {
    port: number;
    host: string;
    shutdownTimeout: number;
  };
  api: {
    basePath: string;
    cors: boolean;
    bodyLimit: string;
    compression: boolean;
    requestLogging: boolean;
    correlationId: boolean;
    helmet?: boolean;
    docs: {
      enabled: boolean;
      endpoint: string;
      title: string;
      version: string;
      description: string;
    };
  };
  database: {
    required: boolean;
    type: string;
    host?: string;
    port?: number;
    user?: string;
    password?: string;
    database?: string;
    url?: string;
    ssl?: boolean;
    maxConnections: number;
    idleTimeout: number;
    connectTimeout: number;
  };
  auth: {
    required: boolean;
    type?: string;
    azure?: {
      enabled?: boolean;
      tenantId?: string;
      clientId?: string;
      clientSecret?: string;
      apiScope?: string;
    };
    jwt?: {
      secret?: string;
      publicKey?: string;
      issuer?: string;
      audience?: string;
    };
  };
  health: {
    enabled: boolean;
    endpoint: string;
    memory: {
      threshold: number;
    };
  };
  logging: {
    level: string;
    prettyPrint: boolean;
  };
}
